'use client';

import { useClientConfig } from '@providers/ClientConfigProvider';
import { RedirectModuleType } from '@lib/sanity/queries/modules/redirect/types';
import { getCookie } from '@utils/cookie';
import { withAnonymousId } from '@utils/withAnonymousId';
import { useSearchParams } from 'next/dist/client/components/navigation';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

const AID_WAIT_TIMEOUT_MS = 1500;
const AID_POLL_INTERVAL_MS = 50;

function waitForAnonymousId(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).anonymousId) return resolve();
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += AID_POLL_INTERVAL_MS;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).anonymousId || elapsed >= timeoutMs) {
        clearInterval(interval);
        resolve();
      }
    }, AID_POLL_INTERVAL_MS);
  });
}

type Props = {
  data: RedirectModuleType;
};

export default function RedirectModule({ data }: Props) {
  const config = useClientConfig();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!data.path) return;
    if (getCookie('loggedInApp') !== 'true') return;

    let cancelled = false;
    waitForAnonymousId(AID_WAIT_TIMEOUT_MS).then(() => {
      if (cancelled) return;
      const queryParams = searchParams.toString();
      const url = `${config.APP_URL}${data.path}`;
      const fullUrl = queryParams ? `${url}?${queryParams}` : url;
      let appOrigin: string | undefined;
      try {
        appOrigin = new URL(config.APP_URL).origin;
      } catch {
        appOrigin = undefined;
      }
      router.replace(withAnonymousId(fullUrl, appOrigin));
    });

    return () => {
      cancelled = true;
    };
  }, [config.APP_URL, data.path, router, searchParams]);

  return <></>;
}
